/**
 * Drawer 'Novo/Editar contrato' (Dialog largo, padrão commitment-drawer):
 * Cliente* → Tipo de contrato* (com atalho '+ novo tipo') → Nome* → Valor* →
 * Dia de vencimento* → Vigência* → Renovação automática → campos condicionais
 * de horas (modalidade 'hours') → Observações técnicas.
 */
import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { MODALITY_LABELS } from '@erp/shared';
import type { Contract, ContractBody } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Select, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Dialog } from '@/components/ui/misc';
import { ClientPicker, type ClientRef } from '@/features/tickets/ClientPicker';
import { contractTypesOptions, errorMessage } from './billing-queries';
import { useCreateContract, useUpdateContract } from './billing-mutations';
import {
  formatBRLInput,
  hhmmToMinutes,
  minutesToHHMM,
  parseBRLInput,
  todayISO,
} from './billing-format';
import { ContractTypesDialog } from './contract-types-dialog';

/** Input de moeda com prefixo R$ e normalização pt-BR no blur (compartilhado
 *  pelas telas de cadastros financeiros do v9). */
export function MoneyInput({
  id,
  value,
  onChange,
  required,
  placeholder = '0,00',
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
}) {
  const invalid = value.trim() !== '' && parseBRLInput(value) === null;
  return (
    <div className="relative">
      <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-muted-foreground">
        R$
      </span>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          const parsed = parseBRLInput(value);
          if (parsed !== null) onChange(formatBRLInput(parsed));
        }}
        placeholder={placeholder}
        inputMode="decimal"
        required={required}
        aria-invalid={invalid || undefined}
        className="pl-9 text-right tabular-nums"
      />
    </div>
  );
}

export function ContractDrawer({
  initial,
  presetClient,
  onClose,
}: {
  initial?: Contract;
  presetClient?: ClientRef;
  onClose: () => void;
}) {
  const [client, setClient] = useState<ClientRef | null>(
    initial ? { id: initial.clientId, name: initial.clientName } : (presetClient ?? null)
  );
  const [contractTypeId, setContractTypeId] = useState(
    initial ? String(initial.contractTypeId) : ''
  );
  const [name, setName] = useState(initial?.name ?? '');
  const [valueText, setValueText] = useState(initial ? formatBRLInput(initial.value) : '');
  const [dueDay, setDueDay] = useState(initial ? String(initial.dueDay) : '10');
  const [startDate, setStartDate] = useState(initial?.startDate ?? todayISO());
  const [expirationDate, setExpirationDate] = useState(initial?.expirationDate ?? '');
  const [automaticRenewal, setAutomaticRenewal] = useState(initial?.automaticRenewal ?? false);
  const [includedHours, setIncludedHours] = useState(
    initial?.includedMinutes != null ? minutesToHHMM(initial.includedMinutes) : ''
  );
  const [surplusText, setSurplusText] = useState(
    initial?.surplusHourValue != null ? formatBRLInput(initial.surplusHourValue) : ''
  );
  const [observations, setObservations] = useState(initial?.technicalObservations ?? '');
  const [typesOpen, setTypesOpen] = useState(false);

  const typesQ = useQuery(contractTypesOptions());
  const allTypes = typesQ.data ?? [];
  // Ativos + o tipo do contrato em edição mesmo se inativado depois.
  const selectableTypes = allTypes.filter(
    (t) => t.active || (initial !== undefined && t.id === initial.contractTypeId)
  );
  const selectedType = allTypes.find((t) => String(t.id) === contractTypeId);
  const isHours = selectedType?.modality === 'hours';

  const create = useCreateContract();
  const update = useUpdateContract(initial?.id ?? 0);
  const mutation = initial ? update : create;

  const parsedValue = parseBRLInput(valueText);
  const parsedSurplus = parseBRLInput(surplusText);
  const parsedIncluded = hhmmToMinutes(includedHours);
  const dueDayNum = Number(dueDay);
  const badRange = expirationDate !== '' && startDate !== '' && expirationDate < startDate;

  const valid =
    client !== null &&
    contractTypeId !== '' &&
    name.trim() !== '' &&
    parsedValue !== null &&
    dueDay !== '' &&
    Number.isInteger(dueDayNum) &&
    dueDayNum >= 1 &&
    dueDayNum <= 28 &&
    startDate !== '' &&
    !badRange &&
    (!isHours || (parsedIncluded !== null && parsedSurplus !== null));

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || client === null || parsedValue === null) return;
    const body: ContractBody = {
      clientId: client.id,
      contractTypeId: Number(contractTypeId),
      name: name.trim(),
      value: parsedValue,
      dueDay: dueDayNum,
      startDate,
      expirationDate: expirationDate || null,
      automaticRenewal,
      includedMinutes: isHours ? parsedIncluded : null,
      surplusHourValue: isHours ? parsedSurplus : null,
      technicalObservations: observations.trim() || null,
    };
    mutation.mutate(body, { onSuccess: onClose });
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={initial ? 'Editar contrato' : 'Novo contrato'}
      className="sm:max-w-2xl"
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label>
            Cliente <span className="text-destructive">*</span>
          </Label>
          <ClientPicker selected={client} onSelect={setClient} />
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="contract-type">
              Tipo de contrato <span className="text-destructive">*</span>
            </Label>
            <button
              type="button"
              onClick={() => setTypesOpen(true)}
              className="flex items-center gap-1 text-[13px] font-medium text-primary transition-colors hover:text-primary-pressed"
            >
              <Plus className="size-3.5" />
              novo tipo
            </button>
          </div>
          <Select
            id="contract-type"
            value={contractTypeId}
            onChange={(e) => setContractTypeId(e.target.value)}
            disabled={typesQ.isPending}
            required
          >
            <option value="" disabled>
              {typesQ.isPending ? 'Carregando tipos…' : 'Selecione o tipo…'}
            </option>
            {selectableTypes.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} — {MODALITY_LABELS[t.modality]}
              </option>
            ))}
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="contract-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="contract-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Suporte mensal"
            required
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="contract-value">
              Valor <span className="text-destructive">*</span>
            </Label>
            <MoneyInput id="contract-value" value={valueText} onChange={setValueText} required />
            {valueText.trim() !== '' && parsedValue === null ? (
              <p className="text-xs text-destructive">Valor inválido. Use o formato 1.234,56.</p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contract-due-day">
              Dia de vencimento <span className="text-destructive">*</span>
            </Label>
            <Input
              id="contract-due-day"
              type="number"
              min={1}
              max={28}
              value={dueDay}
              onChange={(e) => setDueDay(e.target.value)}
              className="w-24 tabular-nums"
              required
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>
            Vigência <span className="text-destructive">*</span>
          </Label>
          <div className="grid grid-cols-2 gap-2">
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              aria-label="Início da vigência"
              required
            />
            <Input
              type="date"
              value={expirationDate}
              onChange={(e) => setExpirationDate(e.target.value)}
              aria-label="Expiração (opcional)"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Deixe a expiração em branco para contrato sem prazo.
          </p>
          {badRange ? (
            <p className="text-xs text-destructive">
              A expiração deve ser igual ou depois do início.
            </p>
          ) : null}
        </div>

        <label className="flex w-fit cursor-pointer items-center gap-2 text-sm">
          <Switch
            checked={automaticRenewal}
            onCheckedChange={setAutomaticRenewal}
            aria-label="Renovação automática"
          />
          Renovação automática
        </label>

        {isHours ? (
          <div className="grid gap-4 rounded-md border border-primary/30 bg-primary/5 p-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="contract-included-hours">
                Horas incluídas/mês <span className="text-destructive">*</span>
              </Label>
              <Input
                id="contract-included-hours"
                value={includedHours}
                onChange={(e) => setIncludedHours(e.target.value)}
                placeholder="40:00"
                className="tabular-nums"
                required
              />
              {includedHours.trim() !== '' && parsedIncluded === null ? (
                <p className="text-xs text-destructive">Use o formato HH:MM (ex.: 40:00).</p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contract-surplus">
                Valor hora excedente <span className="text-destructive">*</span>
              </Label>
              <MoneyInput
                id="contract-surplus"
                value={surplusText}
                onChange={setSurplusText}
                required
              />
              {surplusText.trim() !== '' && parsedSurplus === null ? (
                <p className="text-xs text-destructive">Valor inválido. Use o formato 1.234,56.</p>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="contract-observations">Observações técnicas</Label>
          <Textarea
            id="contract-observations"
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
          />
        </div>

        {mutation.error ? (
          <p className="text-sm text-destructive">{errorMessage(mutation.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={mutation.isPending || !valid}>
            {mutation.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>

      {typesOpen ? <ContractTypesDialog onClose={() => setTypesOpen(false)} /> : null}
    </Dialog>
  );
}
